import {
    Controllers,
    ProblemResult,
    IsBadRequestResult,
    IsServerErrorResult,
    APICallResult,
    IsNotFoundResult,
    IsForbiddenResult
} from "@infoland/iprova-api";
import { RootState } from "@/store";
import { ActionTree, Dispatch, GetterTree, Module, MutationTree } from "vuex";
import { ActionResult } from "@/store/modules/tasks/tasks";
import { TaskFilterOption } from "@/enums";
import { loadMyNotificationsAsync, Task } from "@/tasks";
import storeHelper from "@/store/store-helper";
import Vue from "vue";

// state type
export interface NotificationsState {
    notifications: Task[];
    filteredNotifications: Task[];
    selectedFilter: TaskFilterOption;
}

export function initialize(): NotificationsState {
    return {
        notifications: [],
        filteredNotifications: [],
        selectedFilter: TaskFilterOption.My
    };
}

// initial state
const state: NotificationsState = initialize();

// getters for this store
const getters: GetterTree<NotificationsState, RootState> = {
    notifications(state: NotificationsState): Task[] {
        return state.notifications;
    },
    filteredNotifications(state: NotificationsState): Task[] {
        return state.filteredNotifications;
    },
    selectedFilter(state: NotificationsState): TaskFilterOption {
        return state.selectedFilter;
    }
};

const mutations: MutationTree<NotificationsState> = {
    _notifications(state: NotificationsState, notifications: Task[]): void {
        state.notifications = notifications;
    },
    _updateReadState(state: NotificationsState, { notification_id, isRead }: { notification_id: string; isRead: boolean }): void {
        const task = state.notifications.find(t => t.id == notification_id);
        if (task) task.read = isRead;
    },
    _filteredNotifications(state: NotificationsState, filteredNotifications: Task[]): void {
        state.filteredNotifications = filteredNotifications;
    },
    _selectedFilter(state: NotificationsState, filter: TaskFilterOption): void {
        state.selectedFilter = filter;
    }
};

function handleNotificationsMutationResult(result: void | ProblemResult<string>, dispatch: Dispatch): ActionResult<void, string> {
    if (IsServerErrorResult(result) || IsBadRequestResult(result) || IsNotFoundResult(result) || IsForbiddenResult(result)) {
        return { success: false };
    } else {
        return { success: true };
    }
}

const actions: ActionTree<NotificationsState, RootState> = {
    async loadNotificationsAsync({ commit }): Promise<void> {
        const storeNotifications = await loadMyNotificationsAsync();

        commit("_notifications", storeNotifications);
        commit("_filteredNotifications", storeNotifications);
    },
    // Update notification read property
    async updateReadNotificationsAsync({ commit, state }, { notification_id, isRead }): Promise<void> {
        await Controllers.GenericController.patchAsync<{ read: boolean }, void>(`notifications/${notification_id}`, {
            read: isRead
        });
        commit("_updateReadState", { notification_id, isRead });
    },
    // Update notification is_important property
    async updateIsImportantNotificationAsync({ dispatch, commit, state }, { notification_id, is_important }): Promise<void> {
        const result = await Controllers.GenericController.patchAsync<{ is_important: boolean }, void | ProblemResult<string>>(
            `notifications/${notification_id}`,
            {
                is_important: is_important
            }
        );
        if (handleNotificationsMutationResult(result, dispatch).success) {
            const notifications = [...state.notifications];
            notifications.find(x => x.id == notification_id).is_important = is_important;
            commit("_notifications", notifications);
        }
    },
    // Update the filtered notifications list
    updateFilteredNotifications({ commit, state }, { notifications }): void {
        commit("_filteredNotifications", notifications);
    },
    // Update the selected filter
    updateSelectedFilter({ commit, state }, { filter }): void {
        commit("_selectedFilter", filter);
    },
    async deleteNotificationAsync({ commit, dispatch, state }, { notification_id }): Promise<void> {
        const result = await Controllers.GenericController.deleteAsync<null, void | ProblemResult<string>>(`notifications/${notification_id}`);

        if (handleNotificationsMutationResult(result, dispatch).success) {
            commit(
                "_notifications",
                state.notifications.filter(x => x.id != notification_id)
            );
        }
    }
};

// export the namespace
export const namespace = "notifications";

// export the store 'module'
export default <Module<NotificationsState, RootState>>{
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
